import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import { isValidIban } from '@utils/iban';
import {
  BillingCadence,
  CarrierRole,
  Country,
  Language,
  PaymentTerm,
  SelfBillingMode,
  SupplierDocumentType,
  SupplierDriverDocumentType,
  SupplierTruckDocumentType,
} from 'src/types/enums';

import type { ListSupplierDriversResponse, SupplierDriver } from './endpoints/ListSupplierDriversEndpoint.types';
import type { SupplierListItem, SuppliersResponse } from './endpoints/SuppliersEndpoint.types';
import type { SupplierExportItem, SuppliersForExportResponse } from './endpoints/SuppliersForExportEndpoint.types';
import type { SupplierByIdResponse, SupplierDetail } from './endpoints/SupplierEndpoint.types';
import type { CarrierNameByInviteLinkResponse } from './endpoints/CarrierNameByInviteLinkEndpoint.types';
import type {
  CheckIbanBody,
  CheckIbanResponse,
  RequestBankAccountChangeBody,
  ReviewBankAccountChangeBody,
  SupplierBankAccountResponse,
} from './endpoints/SupplierBankAccountEndpoints.types';
import type { ExactImportResult } from './endpoints/ExactAccountsImportEndpoint.types';
import type { CreateSupplierResponse } from './endpoints/CreateSupplierEndpoint.types';
import type { UpdateSupplierResponse } from './endpoints/UpdateSupplierEndpoint.types';
import type { CreateSupplierContactResponse } from './endpoints/CreateSupplierContactEndpoint.types';
import type { UpdateSupplierContactResponse } from './endpoints/UpdateSupplierContactEndpoint.types';
import type { DeleteSupplierContactsResponse } from './endpoints/DeleteSupplierContactsEndpoint.types';
import type { LinkSupplierDocumentResponse } from './endpoints/LinkSupplierDocumentEndpoint.types';
import type { LinkSupplierDriverDocumentResponse } from './endpoints/LinkSupplierDriverDocumentEndpoint.types';
import type { CreateSupplierTruckResponse } from './endpoints/CreateSupplierTruckEndpoint.types';
import type { UpdateSupplierTruckResponse } from './endpoints/UpdateSupplierTruckEndpoint.types';
import type { UpdateSupplierTruckIndexesResponse } from './endpoints/UpdateSupplierTruckIndexesEndpoint.types';
import type { LinkSupplierTruckDocumentResponse } from './endpoints/LinkSupplierTruckDocumentEndpoint.types';
import type { InviteCarrierUserResponse } from './endpoints/InviteCarrierUserEndpoint.types';
import type { DeleteCarrierUserInviteResponse } from './endpoints/DeleteCarrierUserInviteEndpoint.types';
import type { AcceptCarrierUserInviteResponse } from './endpoints/AcceptCarrierUserInviteEndpoint.types';
import type {
  UpdateSupplierTruckAppPasswordBody,
  UpdateSupplierTruckAppPasswordResponse,
} from './endpoints/UpdateSupplierTruckAppPasswordEndpoint.types';

export type { SupplierDriver };

// Cleaner re-exports for consumers.
export type SupplierListEntry = SupplierListItem;
export type SupplierExportEntry = SupplierExportItem;
export type SupplierDetailEntry = SupplierDetail;

export const SUPPLIERS_SWR_KEY = 'suppliers';
export const SUPPLIER_SWR_KEY = 'supplier';
export const CARRIER_NAME_BY_INVITE_LINK_SWR_KEY = 'carrier-name-by-invite-link';

export async function listSupplierDrivers(supplierId: number, search?: string): Promise<ListSupplierDriversResponse> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  const qs = params.toString();
  return fetchEndpointData<ListSupplierDriversResponse>(`/api/v1/suppliers/${supplierId}/drivers${qs ? `?${qs}` : ''}`);
}

// ─── Queries ─────────────────────────────────────────────────────────

export interface SuppliersFilters {
  search?: string;
  isActive?: boolean;
  isRegularCarrier?: boolean;
  hasOpenOrders?: boolean;
  language?: Language;
  missingIban?: boolean;
}

function buildSuppliersParams(cursor?: number | null, take?: number, filters?: SuppliersFilters): string {
  const params = new URLSearchParams();
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.isActive != null) params.set('isActive', String(filters.isActive));
  if (filters?.isRegularCarrier != null) params.set('isRegularCarrier', String(filters.isRegularCarrier));
  if (filters?.hasOpenOrders != null) params.set('hasOpenOrders', String(filters.hasOpenOrders));
  if (filters?.language) params.set('language', filters.language);
  if (filters?.missingIban) params.set('missingIban', 'true');
  return params.toString();
}

export async function getSuppliers(
  cursor?: number | null,
  take?: number,
  filters?: SuppliersFilters,
): Promise<SuppliersResponse> {
  const qs = buildSuppliersParams(cursor, take, filters);
  return fetchEndpointData<SuppliersResponse>(`/api/v1/suppliers${qs ? `?${qs}` : ''}`);
}

export async function getSuppliersForExport(
  cursor?: number | null,
  take?: number,
  filters?: SuppliersFilters,
): Promise<SuppliersForExportResponse> {
  const qs = buildSuppliersParams(cursor, take, filters);
  return fetchEndpointData<SuppliersForExportResponse>(`/api/v1/suppliers/export${qs ? `?${qs}` : ''}`);
}

export async function getSupplierById(id: number): Promise<SupplierByIdResponse> {
  return fetchEndpointData<SupplierByIdResponse>(`/api/v1/suppliers/${id}`);
}

export async function getCarrierNameByInviteLink(
  carrierId: number,
  email: string,
  token: string,
): Promise<CarrierNameByInviteLinkResponse> {
  const params = new URLSearchParams({ carrierId: carrierId.toString(), email, token });
  return fetchEndpointData<CarrierNameByInviteLinkResponse>(
    `/api/v1/suppliers/carrier-name-by-invite-link?${params.toString()}`,
  );
}

// ─── Query hooks ─────────────────────────────────────────────────────

// Non-suspense (matches the previous urql `context: { suspense: false }` on the
// combobox and the defensive `data?.suppliers ?? []` reads on the planning /
// stats pages). Reads `data` defensively.
export function useSuppliers(filters: SuppliersFilters, take?: number) {
  return useSWR(
    [
      SUPPLIERS_SWR_KEY,
      filters.search,
      filters.isActive,
      filters.isRegularCarrier,
      filters.hasOpenOrders,
      filters.language,
      take,
    ],
    () => getSuppliers(undefined, take, filters),
  );
}

export function useSupplierById(id: number) {
  return useSWR([SUPPLIER_SWR_KEY, id], () => getSupplierById(id));
}

export function useCarrierNameByInviteLink(carrierId: number, email: string, token: string) {
  return useSWR([CARRIER_NAME_BY_INVITE_LINK_SWR_KEY, carrierId, email, token], () =>
    getCarrierNameByInviteLink(carrierId, email, token),
  );
}

// ─── Mutations (fetchers throw on error) ─────────────────────────────

export interface CreateSupplierInput {
  accountingId?: string;
  name: string;
  vatNumber: string;
  companyNumber: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  country: Country;
  language: Language;
  paymentTerm: PaymentTerm;
  internalNotes: string;
  truckAmount: number;
  tautlinerAmount: number;
  boxTrailerAmount: number;
  openTrailerAmount: number;
  defaultVatRateId: string;
  selfBillingMode?: SelfBillingMode;
  iban?: string;
  bic?: string;
  emailEnabled?: boolean;
  peppolEnabled?: boolean;
  regularCarrier: boolean;
  billingCadence?: BillingCadence;
  disableCmrReminders?: boolean;
  dieselSurchargeEnabled?: boolean;
  hasCustomDieselSurcharge?: boolean;
  dieselSurchargePercentage?: number;
}

export async function createSupplier(data: CreateSupplierInput): Promise<CreateSupplierResponse> {
  return fetchEndpointData<CreateSupplierResponse>('/api/v1/suppliers', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateSupplierInput {
  accountingId?: string;
  name?: string;
  vatNumber?: string;
  companyNumber?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: Country;
  language?: Language;
  paymentTerm?: PaymentTerm;
  internalNotes?: string;
  truckAmount?: number;
  tautlinerAmount?: number;
  boxTrailerAmount?: number;
  openTrailerAmount?: number;
  deactivationReason?: string | null;
  defaultVatRateId?: string;
  selfBillingMode?: SelfBillingMode;
  emailEnabled?: boolean;
  peppolEnabled?: boolean;
  regularCarrier?: boolean;
  billingCadence?: BillingCadence;
  disableCmrReminders?: boolean;
  dieselSurchargeEnabled?: boolean;
  hasCustomDieselSurcharge?: boolean;
  dieselSurchargePercentage?: number;
}

export async function updateSupplier(id: number, data: UpdateSupplierInput): Promise<UpdateSupplierResponse> {
  return fetchEndpointData<UpdateSupplierResponse>(`/api/v1/suppliers/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export interface CreateSupplierContactInput {
  name: string;
  email: string;
  phone: string;
  function: string;
  language: Language;
  supplierId: number;
  internalNotes: string;
  shouldReceiveTransportOrders: boolean;
  shouldReceiveSelfBillings?: boolean;
}

export async function createSupplierContact(data: CreateSupplierContactInput): Promise<CreateSupplierContactResponse> {
  return fetchEndpointData<CreateSupplierContactResponse>('/api/v1/suppliers/contacts', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateSupplierContactInput {
  name?: string;
  email?: string;
  phone?: string;
  function?: string;
  language?: Language;
  internalNotes?: string;
  shouldReceiveTransportOrders?: boolean;
  shouldReceiveSelfBillings?: boolean;
}

export async function updateSupplierContact(
  id: number,
  data: UpdateSupplierContactInput,
): Promise<UpdateSupplierContactResponse> {
  return fetchEndpointData<UpdateSupplierContactResponse>(`/api/v1/suppliers/contacts/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteSupplierContacts(ids: number[]): Promise<DeleteSupplierContactsResponse> {
  return fetchEndpointData<DeleteSupplierContactsResponse>('/api/v1/suppliers/contacts', {
    method: 'DELETE',
    body: { ids },
  });
}

export async function linkSupplierDocument(
  supplierId: number,
  documentId: string,
  type: SupplierDocumentType,
): Promise<LinkSupplierDocumentResponse> {
  return fetchEndpointData<LinkSupplierDocumentResponse>(`/api/v1/suppliers/${supplierId}/documents`, {
    method: 'POST',
    body: { documentId, type },
  });
}

export async function unlinkSupplierDocument(
  supplierId: number,
  documentId: string,
  type: SupplierDocumentType,
): Promise<LinkSupplierDocumentResponse> {
  return fetchEndpointData<LinkSupplierDocumentResponse>(`/api/v1/suppliers/${supplierId}/documents`, {
    method: 'DELETE',
    body: { documentId, type },
  });
}

export async function linkSupplierDriverDocument(
  supplierId: number,
  userId: string,
  documentId: string,
  type: SupplierDriverDocumentType,
): Promise<LinkSupplierDriverDocumentResponse> {
  return fetchEndpointData<LinkSupplierDriverDocumentResponse>(
    `/api/v1/suppliers/${supplierId}/drivers/${userId}/documents`,
    {
      method: 'POST',
      body: { documentId, type },
    },
  );
}

export async function unlinkSupplierDriverDocument(
  supplierId: number,
  userId: string,
  documentId: string,
  type: SupplierDriverDocumentType,
): Promise<LinkSupplierDriverDocumentResponse> {
  return fetchEndpointData<LinkSupplierDriverDocumentResponse>(
    `/api/v1/suppliers/${supplierId}/drivers/${userId}/documents`,
    {
      method: 'DELETE',
      body: { documentId, type },
    },
  );
}

export interface CreateSupplierTruckInput {
  name: string;
  licensePlate: string;
  isActive: boolean;
  insuranceExpiresAt: string;
  examinationExpiresAt: string;
  internalNotes: string;
  driverDisplayName?: string;
  supplierId: number;
  plannerId: string;
}

export async function createSupplierTruck(data: CreateSupplierTruckInput): Promise<CreateSupplierTruckResponse> {
  return fetchEndpointData<CreateSupplierTruckResponse>('/api/v1/suppliers/trucks', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateSupplierTruckInput {
  name?: string;
  licensePlate?: string;
  isActive?: boolean;
  insuranceExpiresAt?: string;
  examinationExpiresAt?: string;
  internalNotes?: string;
  driverDisplayName?: string;
  plannerId?: string;
}

export async function updateSupplierTruck(
  id: string,
  data: UpdateSupplierTruckInput,
): Promise<UpdateSupplierTruckResponse> {
  return fetchEndpointData<UpdateSupplierTruckResponse>(`/api/v1/suppliers/trucks/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function updateSupplierTruckIndexes(
  supplierId: number,
  truckIds: string[],
  indexes: number[],
): Promise<UpdateSupplierTruckIndexesResponse> {
  return fetchEndpointData<UpdateSupplierTruckIndexesResponse>(`/api/v1/suppliers/${supplierId}/truck-indexes`, {
    method: 'PATCH',
    body: { truckIds, indexes },
  });
}

export async function linkSupplierTruckDocument(
  supplierTruckId: string,
  documentId: string,
  type: SupplierTruckDocumentType,
): Promise<LinkSupplierTruckDocumentResponse> {
  return fetchEndpointData<LinkSupplierTruckDocumentResponse>(`/api/v1/suppliers/trucks/${supplierTruckId}/documents`, {
    method: 'POST',
    body: { documentId, type },
  });
}

export async function inviteCarrierUser(
  carrierId: number,
  email: string,
  role?: string,
): Promise<InviteCarrierUserResponse> {
  return fetchEndpointData<InviteCarrierUserResponse>(`/api/v1/suppliers/${carrierId}/invites`, {
    method: 'POST',
    body: { email, role },
  });
}

export async function deleteCarrierUserInvite(
  carrierId: number,
  inviteId: string,
): Promise<DeleteCarrierUserInviteResponse> {
  return fetchEndpointData<DeleteCarrierUserInviteResponse>(`/api/v1/suppliers/${carrierId}/invites/${inviteId}`, {
    method: 'DELETE',
  });
}

export async function acceptCarrierUserInvite(
  carrierId: number,
  email: string,
  token: string,
  password: string,
): Promise<AcceptCarrierUserInviteResponse> {
  return fetchEndpointData<AcceptCarrierUserInviteResponse>(`/api/v1/suppliers/${carrierId}/accept-invite`, {
    method: 'POST',
    body: { email, token, password },
  });
}

// Re-export enums used by callers, so they can import from `../models` instead of
// the generated graphql module (matches the existing module-local convention).
export {
  BillingCadence,
  CarrierRole,
  Country,
  Language,
  PaymentTerm,
  SupplierDocumentType,
  SupplierDriverDocumentType,
  SupplierTruckDocumentType,
};

export async function updateSupplierTruckAppPassword(
  supplierId: number,
  truckId: string,
  body: UpdateSupplierTruckAppPasswordBody,
): Promise<UpdateSupplierTruckAppPasswordResponse> {
  return fetchEndpointData<UpdateSupplierTruckAppPasswordResponse>(
    `/api/v1/suppliers/${supplierId}/trucks/${truckId}/app-password`,
    {
      method: 'PUT',
      body,
    },
  );
}

// ─── Bank account (four-eyes) ────────────────────────────────────────

export const SUPPLIER_BANK_ACCOUNT_SWR_KEY = 'supplier-bank-account';

export async function getSupplierBankAccount(supplierId: number): Promise<SupplierBankAccountResponse> {
  return fetchEndpointData<SupplierBankAccountResponse>(`/api/v1/suppliers/${supplierId}/bank-account`);
}

function getSupplierBankAccountSWR([, supplierId]: [string, number]) {
  return getSupplierBankAccount(supplierId);
}

export function useSupplierBankAccount(supplierId: number) {
  return useSWR([SUPPLIER_BANK_ACCOUNT_SWR_KEY, supplierId] as [string, number], getSupplierBankAccountSWR);
}

export async function requestSupplierBankAccountChange(
  supplierId: number,
  body: RequestBankAccountChangeBody,
): Promise<void> {
  await fetchEndpointData(`/api/v1/suppliers/${supplierId}/bank-account-changes`, { method: 'POST', body });
}

export async function reviewSupplierBankAccountChange(
  changeId: string,
  body: ReviewBankAccountChangeBody,
): Promise<void> {
  await fetchEndpointData(`/api/v1/suppliers/bank-account-changes/${changeId}/review`, { method: 'POST', body });
}

export async function checkSupplierIban(body: CheckIbanBody): Promise<CheckIbanResponse> {
  return fetchEndpointData<CheckIbanResponse>('/api/v1/suppliers/iban-check', { method: 'POST', body });
}

type IbanCheckKey = [string, string, string, number];

function checkSupplierIbanSWR([, iban, country, supplierId]: IbanCheckKey) {
  return checkSupplierIban({ iban, country, supplierId });
}

/** Non-blocking warnings for an IBAN being entered; only fetched once the IBAN is valid. */
export function useSupplierIbanCheck(iban: string, country: string, supplierId: number) {
  const key: IbanCheckKey | null = isValidIban(iban) ? ['supplier-iban-check', iban, country, supplierId] : null;
  return useSWR(key, checkSupplierIbanSWR, { revalidateOnFocus: false });
}

/** Uploads a proof document (PDF or image) and returns its id. */
export async function uploadBankAccountProof(file: globalThis.File): Promise<string> {
  const formData = new FormData();
  formData.set('name', file.name);
  formData.set('file', file);
  const result = await fetch('/api/document/upload', { body: formData, method: 'post' });
  const text = await result.text();
  if (!result.ok) {
    throw new Error('Upload van het bewijsstuk mislukt');
  }
  return JSON.parse(text).data.id as string;
}

// ─── Exact accounts import (admin) ───────────────────────────────────

async function postExactImport(path: 'preview' | 'apply', file: globalThis.File): Promise<ExactImportResult> {
  const formData = new FormData();
  formData.set('file', file);
  const result = await fetch(`/api/v1/suppliers/exact-import/${path}`, { body: formData, method: 'post' });
  const text = await result.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text);
  } catch {
    // Non-JSON error body; fall through to the generic message.
  }
  if (!result.ok) {
    const body = (json ?? {}) as { message?: unknown; error?: unknown };
    const error = body.error as { message?: unknown } | string | undefined;
    const message = typeof error === 'string' ? error : (error?.message ?? body.message);
    throw new Error(typeof message === 'string' ? message : 'Import uit Exact mislukt');
  }
  return json as ExactImportResult;
}

export function previewExactAccountsImport(file: globalThis.File): Promise<ExactImportResult> {
  return postExactImport('preview', file);
}

export function applyExactAccountsImport(file: globalThis.File): Promise<ExactImportResult> {
  return postExactImport('apply', file);
}
